//import EventStream from "Models/EventStream";
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import Phaser from 'phaser';
import {
  CurrentSceneReady,
  ListenAddPhrase,
  ListenForReactEvent,
  StartedPlaying,
  ListenPulseNote,
  ListenChangeTempo,
  ListenSetNoteState,
  ListenStartExercise,
  NotifyReact,
  ReactToExerciseEventType,
  RemoveListener,
  ListenMidiOn,
  ListenMidiOff,
  ListenPauseExercise,
  ShowToast,
  ListenSetCursor,
} from 'Phaser/EventBus';
import CursorMobile from 'Phaser/GameObjects/CursorMobile';
import IPlayable from 'Phaser/GameObjects/Playable';
import { PhraseData, PhraseGraphic } from 'Phaser/GameObjects/StaffLine';
import { PrerenderedPhraseData } from 'Types';
import StaffExercise, { INoteGraphicsDatabase } from './StaffExercise';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import Phrase, { Iterator } from 'Models/Phrase';
import TimeKeeper from 'Models/TimeKeeper';
import * as Tone from 'tone';
import Logger from 'Utils/Logger';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import TimeSignature from 'Models/TimeSignature';

export default class SightReadingMobile
  extends Phaser.Scene
  implements INoteGraphicsDatabase
{
  phrases: PhraseGraphic[] = [];
  cursor?: CursorMobile;
  currentIdx?: number;
  eventStream?: EventStreamPhaser2;
  log?: Phaser.GameObjects.Text;

  timeKeeperAuxiliary?: TimeKeeper;

  phrasesSeen: Map<string, number> = new Map();

  clickAudio?: Phaser.Sound.BaseSound;
  tempo: number = 72;

  lastTickTime?: number;
  lastMeasure?: number;

  debugText?: Phaser.GameObjects.Text;

  // This is a hack for maintaining the correct sequence when new phrases get loaded in
  // should no longer be necessary once we get all the data baked in from the backend
  phraseToBeLoadedNext: {
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
    phraseRaw: Phrase;
  } | null = null;

  pauseQueued = false;
  playbackStarted = false;
  shouldUpdate = false;

  measureCount: number = 0;

  constructor() {
    super('SightReadingMobile');
  }

  create() {
    NotifyReact(CurrentSceneReady(this));
    this.events.once('shutdown', this.destroy, this);
  }

  debugLog(text: string) {
    this.debugText!.setText(text);
  }

  init({ eventStream }: { eventStream: EventStreamPhaser2 }) {
    this.debugText = this.add.text(0, -400, 'DebugLog', {
      fontSize: 34,
      color: '#000000',
    });
    this.timeKeeperAuxiliary = new TimeKeeper();
    //this.cameras.main.setZoom(0.5);
    this.eventStream = eventStream;
    this.phrases = [];
    this.cameras.main.setBackgroundColor('0xebebeb');
    //this.log = this.add.text(0, 0, "log");
    ListenForReactEvent(ListenPauseExercise(this.pauseExercise), this);
    ListenForReactEvent(ListenAddPhrase(this.addPhrase), this, false, true);
    ListenForReactEvent(ListenMidiOn(this.noteOn), this);
    ListenForReactEvent(ListenMidiOff(this.noteOff), this);
    ListenForReactEvent(
      ListenSetNoteState(this.setNoteState),
      this,
      false,
      false
    );
    ListenForReactEvent(ListenPulseNote(this.pulseNote), this, false, false);
    ListenForReactEvent(
      ListenSetNoteState(this.setNoteState),
      this,
      false,
      false
    );
    ListenForReactEvent(
      ListenStartExercise(this.startExercise),
      this,
      false,
      false
    );
    ListenForReactEvent(
      ListenChangeTempo(this.changeTempo),
      this,
      false,
      false
    );
    //ListenForReactEvent(ListenAddPhrase(this.addPhrase), this, false, true);
    ListenForReactEvent(ListenSetCursor(this.setCursor), this);
    this.clickAudio = this.sound.add('click');
  }

  setCursor({ measure }: { measure: number }) {
    const phrase = this.phrases.find(
      (phrase) => measure - phrase.phraseStartingMeasure! < 4
    );
    if (phrase === undefined) {
      console.error(
        "given measure doesn't appear in any of the active phrases!"
      );
    } else {
      const firstNote = phrase.getFirstNotePositionInMeasure(
        measure - phrase.phraseStartingMeasure!
      );
      console.log('measure', measure);
      const cursorStartPos = firstNote!.getPosition().x;
      const numMeasuresIn = measure - phrase.phraseStartingMeasure!;
      const timesig = phrase._data.timeSignature;
      this.cursor?.setStart(
        cursorStartPos,
        (timesig.numerator - 1) * timesig.denominator * numMeasuresIn,
        measure
      );
    }
  }

  changeTempo({ tempo }: { tempo: number }) {
    console.debug('changed tempo ', tempo);
    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);
  }

  metronomeClick() {
    if (this.clickAudio === undefined) {
      Logger.warn('metronome audio not loaded!');
    }
    this.clickAudio?.stop();
    this.clickAudio?.play();
  }

  startExercise({ tempo }: { tempo?: number }) {
    if (tempo) {
      this.tempo = tempo;
    }
    this.shouldUpdate = true;
    if (!this.playbackStarted) {
      //Tone.start();
      this.metronomeClick();
      const secondsPerMeasure = (60 / this.tempo) * 4;
      const measureOffset =
        TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      console.debug('starting bpm ', this.tempo);
      Tone.start();
      //NotifyReact(StartedPlaying());
      this.timeKeeperAuxiliary!.setTimeSignatures(0, [new TimeSignature(4, 4)]);
      this.timeKeeperAuxiliary!.resetTime();
      this.timeKeeperAuxiliary!.unpause(
        this.tempo,
        0,
        measureOffset,
        NoteType._32nd
      );
      this.cursor?.blink((60 / this.tempo) * 1000);
    } else {
      NotifyReact(StartedPlaying());
      this.cursor?.unpause();
    }
  }

  update() {
    if (this.pauseQueued) {
      this._pauseExercise();
    }
    if (this.shouldUpdate) {
      this.cursor?.update();
      if (this.playbackStarted) return;
      const timestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      if (this.lastTickTime === undefined) {
        this.lastTickTime =
          timestamp - StaffExercise.METRONOME_SCHEDULE_AHEAD_TIME;
        console.debug('last tick', this.lastTickTime);
      }
      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      if (measureDurationSinceLastTickTime > beatFraction) {
        this.metronomeClick();
        const beatDuration = (60 / (this.tempo ?? 72)) * 1000;
        this.cursor?.blink(beatDuration);
        // Checking here again because lastTickTime gets mutated asynchronously
        // from the onFinishPlaying callback :(
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
      }
    }
  }

  showToast(message: string) {
    console.debug(message);
  }

  addPhrase({
    phraseRaw,
    phrase,
    cursorData,
  }: {
    phraseRaw: Phrase;
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
  }) {
    if (phrase && this.phrasesSeen.get(phrase.uuid) != phrase.startTimestamp) {
      if (phrase.startTimestamp !== 0 && this.phrases.length === 0) {
        this.phraseToBeLoadedNext = { phrase, cursorData, phraseRaw };
        return;
      }
      const x =
        this.currentIdx !== undefined
          ? this.phrases[this.currentIdx].getNextPosition()
          : 100;
      this.currentIdx =
        this.currentIdx !== undefined ? (this.currentIdx + 1) % 4 : 0;
      console.log('index', this.currentIdx);

      console.log('position: ', x, this.currentIdx);
      const phraseGraphic = new PhraseGraphic(
        this,
        x,
        {
          globalScaling: 1,
          pitchLineGap: 20,
          pitchLineWidth: 2,
          measureLineWidth: 3,
          phraseWidth: 1660,
          clefGap: 100,
          doubleEndLines: true,
        },
        phrase,
        'None',
        this.measureCount
        //cursorData
      );
      this.measureCount += 4; // Hack/Jank: This is hardcoded for now, maybe look into making it dynamic
      const scenePaddingVertical = 110;
      const cameraHeight = this.cameras.main.height;
      const staffHeight = phraseGraphic.getHeight();
      const availSpace = cameraHeight - scenePaddingVertical * 2;
      const zoomFactor = staffHeight / availSpace;
      console.log('zoom', zoomFactor);
      this.cameras.main.setZoom(1 / zoomFactor);
      const phraseY =
        this.cameras.main.height / 2 - phraseGraphic.getHeight() / 2;
      phraseGraphic.setPosition(x, phraseY);
      if (this.phrases.length === 0) {
        this.phrases[0] = phraseGraphic;
      } else {
        if (this.phrases.length >= 4 && this.currentIdx !== undefined) {
          console.log('destroyed', this.currentIdx);
          this.phrases[this.currentIdx].destroy();
        }
        this.phrases[this.currentIdx] = phraseGraphic;
      }
      if (!this.cursor) {
        const cursorHeight = phraseGraphic.getHeight() * 1.35;
        const cursorWidth = cursorHeight / 10;
        const cursorY =
          phraseGraphic.getHeight() / 2 - cursorHeight / 2 + phraseGraphic.y;
        const firstNotePosition = phraseGraphic.getFirstNotePosition(0);
        this.cursor = new CursorMobile(
          this,
          firstNotePosition,
          cursorY,
          cursorWidth,
          cursorHeight,
          this.eventStream!,
          new IteratorExtensible(this, [], phraseRaw),
          true
        );
      }
      this.cursor?.addPhrase(cursorData, phrase.uuid);
      this.eventStream?.addEvents2(
        new Iterator(cursorData, phraseRaw),
        phraseRaw.getStartTimestamp()
      );
      this.phrasesSeen.set(phrase.uuid, phrase.startTimestamp);
      console.log('added phrase ', phrase.phraseIdx, this.currentIdx);
      if (this.phraseToBeLoadedNext) {
        const phrase = this.phraseToBeLoadedNext;
        this.phraseToBeLoadedNext = null;
        this.addPhrase(phrase);
      }
      console.debug('adding phrase success');
    } else {
      console.debug(
        'adding phrase failed',
        phrase,
        this.phrasesSeen.has(phrase.uuid)
      );
    }
  }

  getNoteById(id: string, phraseStart?: number): IPlayable | null {
    return (
      this.phrases
        .find((phrase) => phrase._data.startTimestamp === phraseStart)
        ?.getNoteById(id) ?? null
    );
    /*for (let staff of this.staffLines) {
      const found = staff.getNoteById(id);
      if (found) return found;
    }
    return null;*/
  }

  // The update function might still be running when the Puase callback is triggered by
  // the registered event which will mess up the validity of the state.
  // Therefore, we queue a pause request which then immediately gets processed
  // in the next update() tick.
  pauseExercise() {
    this.pauseQueued = true;
  }

  _pauseExercise() {
    this.shouldUpdate = false;
    this.cursor?.clearBlink();
    this.lastTickTime = undefined;
    this.cursor!.pause();
    this.pauseQueued = false;
  }

  getCurrentPhrase() {
    let currentPhrase: PhraseGraphic = this.phrases[0];
    const currentTimestamp =
      this.eventStream!.timeKeeper.audioTimeToMeasureTimestamp();
    for (const phrase of this.phrases) {
      if (phrase._data.startTimestamp > currentTimestamp) continue;
      else if (
        phrase._data.startTimestamp > currentPhrase._data.startTimestamp
      ) {
        currentPhrase = phrase;
      }
    }
    return currentPhrase;
  }

  setNoteState({ id, state }: { id: string; state: ProgressState }) {
    console.debug('setting state');
    this.getNoteById(
      id,
      this.getCurrentPhrase()._data.startTimestamp
    )?.setTimingState(state);
  }

  pulseNote({ id }: { id: string }) {
    this.getNoteById(id, this.getCurrentPhrase()._data.startTimestamp)?.pulse();
  }

  getNotePosition(id: string, timestamp?: number) {
    for (let phrase of this.phrases) {
      const found = phrase.getNotePosition(id, timestamp);
      if (found !== undefined) {
        return found;
      }
    }
    return null;
  }

  getPhraseEndPosition() {
    return Math.max(...this.phrases.map((phrase) => phrase.getNextPosition()));
  }

  noteOn({ note, velocity }: { note: number; velocity: number }) {
    if (!this.playbackStarted && this.eventStream !== undefined) {
      console.debug('trying to start playing');
      const timestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      if (!this.lastTickTime) return;
      const timeDiff = timestamp - this.lastTickTime;
      console.log('timeoffset timediff: ', timestamp, timeDiff);
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        //this.showToast(`Too Late!`);
        NotifyReact(ShowToast('Too late!'));
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        //this.showToast(`Too early!`);
        NotifyReact(ShowToast('Too early!'));
      } else {
        const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.cursor?.unpause(measureOffset);
        console.debug('started playing');
        //this.eventStream!.timeKeeper.resetTime();
        NotifyReact(StartedPlaying({ measureOffset, note, velocity }));
        this.playbackStarted = true;
        this.cursor?.clearBlink();
      }
    }
  }

  noteOff({ note }: { note: number }) {}

  destroy() {
    this.cursor?.destroy(true);
    RemoveListener(ReactToExerciseEventType.SetCursor);
    RemoveListener(ReactToExerciseEventType.AddPhrase);
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
  }
}
